import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader } from 'lucide-react';
import { formatINR } from '../utils/currency';

const BACKEND_URL = (typeof window !== 'undefined' && window.location.origin) || process.env.REACT_APP_BACKEND_URL;

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [orderDetails, setOrderDetails] = useState(null);
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/checkout/status/${sessionId}`,
        { withCredentials: true }
      );

      if (data.payment_status === 'paid') {
        setStatus('success');
        setOrderDetails(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error checking payment status:', error);
      setStatus('error');
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      navigate('/cart');
      return;
    }
    pollPaymentStatus();
  }, [sessionId, navigate, pollPaymentStatus]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-16 h-16 text-purple-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Processing your payment...</h2>
          <p className="text-gray-600">Please wait while we confirm your order</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center" data-testid="order-success">
            <div className="mb-6">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Order Placed Successfully!
            </h1>
            <p className="text-gray-600 mb-8">
              Thank you for your purchase. Your order has been confirmed and is being processed.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h2 className="text-xl font-semibold mb-4">Order Details</h2>
              <div className="space-y-2 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-semibold">
                    {formatINR(orderDetails?.amount_total / 100)} {orderDetails?.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Status:</span>
                  <span className="font-semibold text-green-600">Paid</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition"
              >
                View My Orders
              </button>
              <button
                onClick={() => navigate('/products')}
                className="w-full bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
              >
                Continue Shopping
              </button>
            </div>

            <p className="text-sm text-gray-500 mt-6">
              A confirmation email has been sent to your registered email address.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
          {status === 'expired' ? 'Payment Session Expired' : 'Payment Verification Failed'}
        </h2>
        <p className="text-gray-600 mb-6">
          {status === 'expired'
            ? 'Your payment session has expired. Please try again.'
            : 'We could not verify your payment. Please check your email or contact support.'}
        </p>
        <button
          onClick={() => navigate('/cart')}
          className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition"
        >
          Return to Cart
        </button>
      </div>
    </div>
  );
};

export default OrderSuccess;